import * as React from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { styled, createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import Container from "@mui/material/Container";
import Link from "@mui/material/Link";
import MenuIcon from "@mui/icons-material/Menu";
import MailIcon from "@mui/icons-material/Mail";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import Avatar from "@mui/material/Avatar";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import SignInDesktop from "../SignIn/SignInDesktop";
import ProtectedRoute from "../../ProtectedRoute";
import { getLoggedUser } from "../../Storage/Getter";
import { logoutUser } from "../../Api/UserApi";
import { removeAuthToken, removeInstitution, removeLicense, removeLoggedUser, removeMembership } from "../../Storage/Clear";
import get from "../../Api/Config/GetConfig";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Button from '@mui/material/Button';

const { lazy, Suspense } = React;

// Only needed once logged in / while a dashboard dialog is open; kept out of the landing-page bundle
const LogoHeader = lazy(() => import("./LogoHeader"));
const DashboardDialogs = lazy(() => import("./DashboardDialogs"));

const AboutPage = lazy(() => import("../AboutPage"));
const ContactPage = lazy(() => import("../ContactPage"));
const StudyListScreen = lazy(() => import("./../study-list/StudyListScreen"));
const OutcomeVariablePage = lazy(() => import("../outcome-variable/OutcomeVariablePage"));
const ExplanatoryVariablePage = lazy(() => import("../explanatory-variable/ExplanatoryVariablePage"));
const StudySubTypePage = lazy(() => import("../study-sub-type/StudySubTypePage"));
const StudyTimesPage = lazy(() => import("../study-times/StudyTimesPage"));
const GroupVariablePage = lazy(() => import("../group-variable/GroupVariablePage"));
const StatisticalTestVariablesPage = lazy(() => import("../statistical-test-variables/StatisticalTestVariablesPage"));
const ReviewTestPage = lazy(() => import("../ReviewTestPage"));
const OutputPage = lazy(() => import("../OutputPage"));
const StudyTimesExaminedPage = lazy(() => import("../study-times-examined-page/StudyTimesExaminedPage"));
const ChangePasswordPage = lazy(() => import("../ChangePasswordPage"));
const SignUpDesktop = lazy(() => import("../SignUp/SignUpDesktop"));
const UserProfilePage = lazy(() => import("../UserProfile"));
const TitleOfStudy = lazy(() => import("../title-of-study/TitleOfStudy"));
const StatisticalTests = lazy(() => import("../statistical-tests/StatisticalTests"));
const TimePoints = lazy(() => import("../time-points/TimePoints"));
const Spreadsheet = lazy(() => import("../spread-sheet/SpreadSheet"));
const PrivacyPolicy = lazy(() => import("../PrivacyPolicy"));
const TermsOfUse = lazy(() => import("../TermsOfUse"));
const RefundPolicy = lazy(() => import("../RefundPolicy"));
const PointsTransactionsPage = lazy(() => import("../PointsTransactionsPage"));
const StudyTypePage = lazy(() => import("../study-type/StudyTypeScreen"));
const StudyVariableListPage = lazy(() => import("../StudyVariableListPage"));
const UserList = lazy(() => import("../UserList"));
const SIgnUpBox = lazy(() => import("../SignUp/SIgnUpBox"));
const ForgotPasswordDesktop = lazy(() => import("../SignUp/ForgotPasswordDesktop"));
const ExcelUpload = lazy(() => import("../add-user-excel-upload/ExcelUpload"));
const LicenseView = lazy(() => import("../license-view/LicenseView"));
const MembershipAdmin = lazy(() => import("../membership-admin/MembershipAdmin"));
const Membership = lazy(() => import("../membership/Membership"));
const EditInstitution = lazy(() => import("../edit-instituition/EditInstitution"));
const CreateInstitution = lazy(() => import("../create-institution/CreateInstitution"));
const LicenseInstitutionUsers = lazy(() => import("../license-institution-users/LicenseInstitutionUsers"));
const NotificationList = lazy(() => import("../notification-list/NotificationList"));

const drawerWidth = 230;

// const settings = ["Profile", "Account", "Dashboard", "Logout"];

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const mdTheme = createTheme();

function DashboardContent() {
    const [open, setOpen] = React.useState(false);
    const [anchorElUser, setAnchorElUser] = React.useState(null);
    const [showLeftNavBar, setShowLeftNavBar] = React.useState(false);
    const [pointsAvailable, setPointsAvailable] = React.useState(0);
    const [requiredPoints,setRequiredPoints] = React.useState(0);
    const [finalPoints,setFinalPoints] = React.useState(0);
    const [toastMessage, setToastMessage] = React.useState({
        isOpen: false,
        text: "",
        level: "success",
    });
    const navigate = useNavigate();

    const [openPointsDialog, setOpenPointsDialog] = React.useState(false);
    const [openInsufficient, setOpenInsufficient] = React.useState(false);
    const [runTests, setRunTests] = React.useState(false);
    const [selectedService, setSelectedService] = React.useState({"total_points":0,"services":[]});
    const [openServicesDialog, setOpenServicesDialog] = React.useState(false);

    const handleClickOpenPointsDialog = () => {
        setOpenPointsDialog(true);
    };

    const handleCloseOpenPointsDialog = () => {
        setOpenPointsDialog(false);
    };

    const handleClickOpenIns = () => {
        setOpenInsufficient(true);
    };

    const handleClickCloseIns = () => {
        setOpenInsufficient(false);
    };

    const handleClickOpenServices = () => {
        setOpenServicesDialog(true);
    }

    const handleClickCloseServices = () => {
        setOpenServicesDialog(false);
    }

    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const toggleDrawer = () => {
        setOpen(!open);
    };

    const showNavBar = (val) => {
        setShowLeftNavBar(val);
    };

    const triggerToastMessage = (obj) => {
        setToastMessage(obj);
    };

    const closeToast = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }

        setToastMessage((prevState) => ({
            ...prevState,
            isOpen: false,
        }));
    };

    const logout = () => {
        get(logoutUser()).then(function (result) {
            removeAuthToken();
            removeLoggedUser();
            removeMembership();
            removeLicense();
            removeInstitution();
            showNavBar(false);
            navigate("/");
        });
    };

    const upgradePlan = () => {
        navigate("/plans");
    }

    const changePassword = () => {
        navigate("/changePassword");
    }

    const userProfile = () => {
        let loggedUser = getLoggedUser()
        navigate('/userprofile/' + loggedUser?.id)
    }

    return (
        <>
        {(openPointsDialog || openInsufficient || openServicesDialog) &&
            <Suspense fallback={null}>
                <DashboardDialogs
                    openPointsDialog={openPointsDialog}
                    pointsAvailable={pointsAvailable}
                    setPointsAvailable={setPointsAvailable}
                    requiredPoints={requiredPoints}
                    setRequiredPoints={setRequiredPoints}
                    finalPoints={finalPoints}
                    setFinalPoints={setFinalPoints}
                    handleCloseOpenPointsDialog={handleCloseOpenPointsDialog}
                    handleClickOpenPointsDialog={handleClickOpenPointsDialog}
                    handleClickOpenIns={handleClickOpenIns}
                    handleClickCloseIns={handleClickCloseIns}
                    setRunTests={setRunTests}
                    triggerToastMessage={triggerToastMessage}
                    openInsufficient={openInsufficient}
                    openServicesDialog={openServicesDialog}
                    handleClickCloseServices={handleClickCloseServices}
                    selectedService={selectedService}
                />
            </Suspense>
        }
        <Snackbar
            open={toastMessage.isOpen}
            autoHideDuration={6000}
            onClose={closeToast}
            key={"bottom center"}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
            <Alert
                onClose={closeToast}
                severity={toastMessage.level}
                sx={{ width: "100%" }}
            >
                {toastMessage.text}
            </Alert>
        </Snackbar>
        <ThemeProvider theme={mdTheme}>
            <Box sx={{ display: "flex", height: '10vh' }}>
                <CssBaseline />
                {showLeftNavBar ? (
                    <>
                    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
                        // open={open}
                        style={{ backgroundColor: "#fff" }}
                    >
                        {/* <Toolbar
                            style={{ color: "#04395ecc" }}
                            sx={{
                                pr: "24px", // keep right padding when drawer closed
                            }}
                        >*/}
                        <Suspense fallback={null}>
                        <LogoHeader
                            toggleDrawer={toggleDrawer}
                            toggleOpen={open}
                            pointsAvailable={pointsAvailable}
                            handleClickOpenPointsDialog={handleClickOpenPointsDialog}
                            showLeftNavBar={(status) => showNavBar(status)}
                            selectedService={selectedService}
                            setSelectedService={setSelectedService}
                            handleClickOpenServices={handleClickOpenServices}
                            logoutUser={logout}
                            triggerToastMessage={triggerToastMessage}
                        />
                        </Suspense>
                        {/* <Typography
                                component="h1"
                                variant="h6"
                                color="inherit"
                                noWrap
                                sx={{ flexGrow: 1 }}
                            >
                                Research Module
                            </Typography> */}
                        {/*<IconButton style={{ color: "#04395ecc" }}>
                                <Badge badgeContent={4}>
                                    <NotificationsIcon />
                                </Badge>
                            </IconButton>
                            <IconButton style={{ color: "#04395ecc" }}>
                                <Badge
                                    style={{ color: "#04395ecc" }}
                                    badgeContent={99}
                                >
                                    <MailIcon />
                                </Badge>
                            </IconButton>*/}
                        {/* <Box sx={{ flexGrow: 0 }}>
                                <Tooltip title="Open settings">
                                    <IconButton
                                        onClick={handleOpenUserMenu}
                                        sx={{ p: 0 }}
                                    >
                                        <Avatar
                                            alt="Remy Sharp"
                                            src="/static/images/avatar/2.jpg"
                                        />
                                    </IconButton>onClick={() => setOpenNotifDialog(true)}
                                </Tooltip>
                                <Menu
                                    sx={{ mt: "45px" }}
                                    id="menu-appbar"
                                    anchorEl={anchorElUser}
                                    anchorOrigin={{
                                        vertical: "top",
                                        horizontal: "right",
                                    }}
                                    keepMounted
                                    transformOrigin={{
                                        vertical: "top",
                                        horizontal: "right",
                                    }}
                                    open={Boolean(anchorElUser)}
                                    onClose={handleCloseUserMenu}
                                >
                                    <MenuItem key={"profile"} onClick={userProfile}>
                                        <Typography textAlign="center">
                                            Profile
                                        </Typography>
                                    </MenuItem>
                                    <MenuItem key={"change_password"} onClick={changePassword}>
                                        <Typography textAlign="center">
                                            Change Password
                                        </Typography>
                                    </MenuItem>
                                    <MenuItem key={"upgrade"} onClick={upgradePlan}>
                                        <Typography textAlign="center">
                                            Upgrade Plan
                                        </Typography>
                                    </MenuItem>
                                    <MenuItem key={"logout"} onClick={logout}>
                                        <Typography textAlign="center">
                                            Logout
                                        </Typography>
                                    </MenuItem>
                                </Menu>
                            </Box> */}
                        {/* </Toolbar> */}
                    </AppBar>
                    </>
                ) : null}
                {/* {(showLeftNavBar && isSubscribed) ? (
                    <Drawer open={open}
                        variant="permanent" >
                        <Toolbar />
                        <LeftMenuFrameComponent />
                    </Drawer>
                ) : null} */}
                <Box
                    component="main" style={{ backgroundColor: '#fff' }}
                    sx={{
                        backgroundColor: (theme) =>
                            theme.palette.mode === "light"
                                ? theme.palette.grey[100]
                                : theme.palette.grey[900],
                        flexGrow: 1,
                        // height: "100vh",
                        // overflow: "auto",
                    }}
                >
                    {showLeftNavBar &&
                        <Toolbar />
                    }
                    <Box className='body_box'
                    //     style={{
                    //     backgroundImage: `url(${shodhai_screen})`,
                    //     backgroundSize: 'contain', backgroundRepeat: 'no-repeat',
                    //     maxHeight: '90vh',
                    //     minHeight: '90vh'
                    // }}
                    >
                        <Suspense fallback={<div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'60vh'}}>Loading...</div>}>
                        <Routes>
                            <Route
                                exact
                                path="/"
                                element={
                                    // <SignInPage
                                    //     showNavBar={showNavBar}
                                    //     triggerToastMessage={
                                    //         triggerToastMessage
                                    //     }
                                    // />
                                    <SignInDesktop
                                        showNavBar={showNavBar}
                                        setPointsAvailable={setPointsAvailable}
                                        triggerToastMessage={
                                            triggerToastMessage
                                        }
                                    />
                                }
                            ></Route>
                            <Route
                                exact
                                path="/privacy"
                                element={<PrivacyPolicy />}
                            ></Route>
                            <Route
                                exact
                                path="/terms"
                                element={<TermsOfUse />}
                            ></Route>
                            <Route
                                exact
                                path="/refund"
                                element={<RefundPolicy />}
                            ></Route>
                            <Route
                                exact
                                path="/about"
                                element={<AboutPage />}
                            ></Route>
                            <Route
                                exact
                                path="/contact"
                                element={<ContactPage />}
                            ></Route>
                            <Route
                                exact
                                path="/studyList"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    path="/studyList"
                                    element={<StudyListScreen />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/signup"
                                element={
                                    <SignUpDesktop
                                        triggerToastMessage={
                                            triggerToastMessage
                                        }
                                    />

                                }
                            ></Route>
                            <Route
                                exact
                                path="/addNewUser"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    path="/addNewUser"
                                    element={
                                        <SIgnUpBox
                                            loggedUser={true}
                                            triggerToastMessage={
                                                triggerToastMessage
                                            }
                                        />
                                    }
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/forgotPassword"
                                element={
                                    <ForgotPasswordDesktop
                                        triggerToastMessage={
                                            triggerToastMessage
                                        }
                                    />
                                }
                            ></Route>
                            <Route
                                exact
                                path="/changePassword"
                                element={
                                    <ChangePasswordPage
                                        triggerToastMessage={
                                            triggerToastMessage
                                        }
                                    />
                                }
                            ></Route>
                            <Route
                                exact
                                path="/userprofile/:userId"
                                element={
                                    <UserProfilePage
                                        triggerToastMessage={
                                            triggerToastMessage
                                        }
                                    />
                                }
                            ></Route>
                            <Route
                                exact
                                path="/createStudy"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/createStudy"
                                    element={<TitleOfStudy
                                        triggerToastMessage={
                                            triggerToastMessage
                                        }
                                    />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/study/:studyId"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/study/:studyId"
                                    element={<TitleOfStudy />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/pointsTransactions"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    path="/pointsTransactions"
                                    element={<PointsTransactionsPage />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/studyType/:studyId"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/studyType/:studyId"
                                    element={<StudyTypePage />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/studySubType/:studyId"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/studySubType/:studyId"
                                    element={<StudySubTypePage />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/studyTimes/:studyId"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/studyTimes/:studyId"
                                    element={<StudyTimesPage />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/studyExamining/:studyId"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/studyExamining/:studyId"
                                    element={<StudyTimesExaminedPage />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/timepoints/:studyId"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/timepoints/:studyId"
                                    element={<TimePoints />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/groupVariable/:studyId"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/groupVariable/:studyId"
                                    element={<GroupVariablePage />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/outcomeVariable/:studyId"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/outcomeVariable/:studyId"
                                    element={<OutcomeVariablePage
                                        triggerToastMessage={
                                            triggerToastMessage
                                        }
                                    />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/explanatoryVariable/:studyId"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/explanatoryVariable/:studyId"
                                    element={<ExplanatoryVariablePage />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/spreadSheet/:studyId"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/spreadSheet/:studyId"
                                    element={<Spreadsheet />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/statTests/:studyId"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/statTests/:studyId"
                                    element={<StatisticalTests />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/statTestVariables/:studyId"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/statTestVariables/:studyId"
                                    element={<StatisticalTestVariablesPage
                                        triggerToastMessage={
                                            triggerToastMessage
                                        }
                                    />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/reviewTests/:studyId"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/reviewTests/:studyId"
                                    element={<ReviewTestPage />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/runTests/:studyId"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/runTests/:studyId"
                                    element={<OutputPage
                                        pointsAvailable={pointsAvailable}
                                        setPointsAvailable={setPointsAvailable}
                                        requiredPoints={requiredPoints}
                                        setRequiredPoints={setRequiredPoints}
                                        finalPoints={finalPoints}
                                        setFinalPoints={setFinalPoints}
                                        handleCloseOpenPointsDialog={handleCloseOpenPointsDialog}
                                        handleClickOpenPointsDialog={handleClickOpenPointsDialog}
                                        handleClickOpenIns={handleClickOpenIns}
                                        handleClickCloseIns={handleClickCloseIns}
                                        runTests={runTests}
                                        setRunTests={setRunTests}
                                        triggerToastMessage={
                                            triggerToastMessage
                                        } />
                                    }
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/studyVariableList/:studyId"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/studyVariableList/:studyId"
                                    element={<StudyVariableListPage />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/userList"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/userList"
                                    element={<UserList triggerToastMessage={triggerToastMessage} 
                                    />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/addUserExcelUpload"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/addUserExcelUpload"
                                    element={<ExcelUpload
                                        triggerToastMessage={
                                            triggerToastMessage
                                        }
                                    />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/viewLicense"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/viewLicense"
                                    element={<LicenseView
                                        triggerToastMessage={
                                            triggerToastMessage
                                        }
                                    />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/editInstitution"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/editInstitution"
                                    element={<EditInstitution
                                        triggerToastMessage={
                                            triggerToastMessage
                                        }
                                    />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/createInstitution"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/createInstitution"
                                    element={<CreateInstitution
                                        triggerToastMessage={
                                            triggerToastMessage
                                        }
                                    />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/licenseInstitutionUsers"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/licenseInstitutionUsers"
                                    element={<LicenseInstitutionUsers
                                        triggerToastMessage={
                                            triggerToastMessage
                                        }
                                    />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/listAllNotifications"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/listAllNotifications"
                                    element={<NotificationList
                                        triggerToastMessage={
                                            triggerToastMessage
                                        }
                                    />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/membershipAdmin"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/membershipAdmin"
                                    element={<MembershipAdmin
                                        triggerToastMessage={
                                            triggerToastMessage
                                        }
                                    />}
                                ></Route>
                            </Route>
                            <Route
                                exact
                                path="/membership"
                                element={<ProtectedRoute />}
                            >
                                <Route
                                    exact
                                    path="/membership"
                                    element={<Membership
                                        triggerToastMessage={
                                            triggerToastMessage
                                        }
                                    />}
                                ></Route>
                            </Route>
                        </Routes>
                        </Suspense>
                    </Box>
                </Box>
            </Box>
            {/* {(showLeftNavBar === false) &&
                <footer className="logo-frame">
                    <img
                        className="summitlogo-icon"
                        loading="eager"
                        alt=""
                        src={summitLogo}
                    />
                </footer>} */}
        </ThemeProvider>
    </>
    );
}

export default function Dashboard() {
    return <DashboardContent />;
}
